/**
 * Neue Finanztrends-Leads → erste Webinar-Anmeldung: Anmeldequote binnen 30 Tagen je Eintrittsmonat und
 * die Verteilung der Tage bis zur ersten Anmeldung. Löst den Kern von /webinar-user-verhalten ab.
 * Zählt nur Live-Anmeldungen (rechtzeitig oder Späteinlass); wer sich nur nach Sendestart angemeldet hat,
 * steht getrennt (Till 14.10.2026). Bruchmarke Okt 26: Webinar-Karte auf der FT-DOI-Danke-Seite ab 12.10.2026.
 */
import { FC } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { BarDatum, ResponsiveBar } from '@nivo/bar';
import ChartCard from '../../../components/auswertungen/ChartCard';
import { Legende, TipRow, useVizStyle, achse } from '../../../components/auswertungen/vizStyle';
import { formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { fetchRegistrierung, formatMonat, RegistrierungAntwort } from '../../../helpers/auswertungenData';
import InfoTip from '../../../components/InfoTip';
import { formatNumber } from '../../../helpers/numberUtils';
import { Hinweis, int, Laden, saeulenBreite, useLaden } from './shared';

/** Monat, ab dem die Webinar-Karte auf der DOI-Danke-Seite Anmeldungen bringt (live seit 12.10.2026). */
const DOI_KARTE_MONAT = '2026-10';
const DOI_KARTE_TEXT = 'Hinweis auf Bestätigungsseite ab 12.10.';
/** `padding` der Monats-Säulen — die Bruchlinie liegt in der Mitte der Lücke vor der Säule. */
const SAEULEN_PADDING = 0.45;

const RegistrierungKarte: FC = () => {
  const { data, fehler } = useLaden(fetchRegistrierung);
  const viz = useVizStyle();
  // Auf schmalen Schirmen überlappen 13 Monatsnamen: jeden zweiten zeigen, steiler drehen.
  const schmal = useMediaQuery(useTheme().breakpoints.down('sm'));
  const monatsTicks = (data?.months ?? []).map((m) => formatMonat(m.month)).filter((_, i) => !schmal || i % 2 === 0);

  return (
    <ChartCard
      title="Wie schnell sich neue Newsletter-Abonnenten zum Webinar anmelden"
      howToRead="Je Monat der Newsletter-Anmeldung: welcher Anteil der neuen Abonnenten sich in den ersten 30 Tagen live zu einem Webinar angemeldet hat (vor Beginn oder während des Webinars eingestiegen) — graue Monate sind noch offen. Unter jeder Säule steht, wie viele Webinare in diesen 30 Tagen liefen: Ein Monat mit wenigen Terminen sieht sonst nach schlechten Abonnenten aus. Die gestrichelte Linie markiert den Webinar-Hinweis auf der Bestätigungsseite (seit 12.10.2026). Die Verteilung zeigt, wie viele Tage zwischen Newsletter-Anmeldung und erster Webinar-Anmeldung lagen."
      terms={[
        { label: TERMS.anmeldequote.label, tip: TERMS.anmeldequote.tip },
        { label: TERMS.webinareImFenster.label, tip: TERMS.webinareImFenster.tip },
        { label: 'Offene Monate', tip: 'Ein Monat ist offen, solange seine 30-Tage-Frist für einen Teil der Abonnenten noch läuft. Seine Quote steigt noch und ist grau.' },
        { label: 'Webinar-Hinweis auf der Bestätigungsseite', tip: 'Seit 12.10.2026 bietet die Seite nach der Newsletter-Bestätigung bei Finanztrends direkt ein Webinar zur Anmeldung an. Anmeldungen darüber zählen ganz normal; der Tooltip je Monat zeigt, wie viele es waren. Ab Oktober 2026 ist die Quote deshalb nicht mehr direkt mit den Monaten davor vergleichbar.' },
      ]}
      source="Erstanmeldungen bei Finanztrends (LPS), Webinar-Anmeldungen"
      freshnessKey="leads_first_appearance"
    >
      {!data ? (
        <Laden fehler={fehler} />
      ) : (
        <>
          <Zahlenzeile data={data} />
          {/* minmax(0, 1fr): ein bloßes 1fr nimmt die Mindestbreite der Grafik an (615 px) und schob die Seite bei 550 px auf 703 (Abnahme 25.10.). */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 3 }}>
            <Box>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
                Anmeldequote 30 Tage je Monat der Newsletter-Anmeldung
              </Typography>
              <Legende
                items={[
                  { label: 'abgeschlossen', color: viz.series1 },
                  { label: 'noch offen', color: viz.muted },
                ]}
              />
              <Box sx={{ height: 260, maxWidth: saeulenBreite(data.months.length) }}>
                <ResponsiveBar
                  data={data.months.map((m) => ({ ...m, label: formatMonat(m.month), quote: m.rate_window ?? 0 })) as unknown as BarDatum[]}
                  keys={['quote']}
                  indexBy="label"
                  margin={{ top: 20, right: 8, bottom: schmal ? 66 : 54, left: 48 }}
                  padding={SAEULEN_PADDING}
                  borderRadius={4}
                  colors={({ data: d }) => (d.is_mature ? viz.series1 : viz.muted)}
                  theme={viz.nivo}
                  animate={false}
                  enableGridX={false}
                  enableGridY
                  gridYValues={4}
                  enableLabel={false}
                  axisLeft={{ tickSize: 0, tickPadding: 6, tickValues: 4, format: achse('%') }}
                  axisBottom={{ tickSize: 0, tickPadding: 18, tickRotation: schmal ? -60 : -40, tickValues: monatsTicks }}
                  tooltip={({ data: d }) => (
                    <Box>
                      <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                        Newsletter-Anmeldung {String(d.label)}
                        {!d.is_mature && ' (noch offen)'}
                      </Box>
                      <TipRow color={d.is_mature ? viz.series1 : viz.muted} label="binnen 30 Tagen angemeldet" value={formatPct(Number(d.quote))} />
                      <TipRow label="neue Abonnenten" value={int(Number(d.cohort))} />
                      {d.webinare_im_fenster !== undefined && <TipRow label={TERMS.webinareImFenster.label} value={int(Number(d.webinare_im_fenster))} />}
                      <TipRow label="binnen 30 Tagen" value={int(Number(d.within_window))} />
                      {Number(d.within_window_doi) > 0 && <TipRow label="davon über den Hinweis auf der Bestätigungsseite" value={int(Number(d.within_window_doi))} />}
                      <TipRow label="vorher schon angemeldet" value={int(Number(d.registered_before))} />
                      {Number(d.after_start_window) > 0 && (
                        <TipRow label="nur nach Sendestart angemeldet (zählt nicht)" value={int(Number(d.after_start_window))} />
                      )}
                    </Box>
                  )}
                  layers={[
                    'grid',
                    'axes',
                    'bars',
                    // Werte an die Säulen (P1-V3 § 2.7) und die Zahl der Webinare im 30-Tage-Fenster darunter (klein).
                    ({ bars, innerHeight }) => (
                      <g pointerEvents="none">
                        {bars.map((b) => (
                          <g key={b.key}>
                            {!schmal && (
                              <text x={b.x + b.width / 2} y={b.y - 4} textAnchor="middle" fontSize={10} fill={viz.inkSecondary}>
                                {formatNumber(Number(b.data.value), 1)}
                              </text>
                            )}
                            {b.data.data.webinare_im_fenster !== undefined && (
                              <text x={b.x + b.width / 2} y={innerHeight + 12} textAnchor="middle" fontSize={10} fill={viz.inkSecondary}>
                                {String(b.data.data.webinare_im_fenster)}
                              </text>
                            )}
                          </g>
                        ))}
                      </g>
                    ),
                    // Bruchmarke: gestrichelte Linie in der Lücke vor dem Oktober 2026, Text im oberen Rand. Der
                    // Text steht links der Linie, solange sie in der rechten Hälfte liegt (sonst rechts) —
                    // bei 390 px liegt Okt 26 ganz rechts, ein rechtsbündiger Text liefe aus dem Bild.
                    ({ bars, innerWidth, innerHeight }) => {
                      const b = bars.find((x) => x.data.data.month === DOI_KARTE_MONAT);
                      if (!b) return null;
                      const luecke = (b.width / (1 - SAEULEN_PADDING)) * SAEULEN_PADDING;
                      const x = b.x - luecke / 2;
                      const links = x > innerWidth / 2;
                      return (
                        <g pointerEvents="none">
                          <line x1={x} x2={x} y1={-12} y2={innerHeight} stroke={viz.inkSecondary} strokeWidth={1} strokeDasharray="4 3" />
                          <text x={links ? x - 4 : x + 4} y={-6} textAnchor={links ? 'end' : 'start'} fontSize={11} fill={viz.inkSecondary}>
                            {DOI_KARTE_TEXT}
                          </text>
                        </g>
                      );
                    },
                  ]}
                  role="img"
                  ariaLabel="Anmeldequote binnen 30 Tagen je Eintrittsmonat"
                />
              </Box>
            </Box>
            <Box>
              <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
                Tage bis zur ersten Webinar-Anmeldung
              </Typography>
              <Box sx={{ height: 24, mb: 1, fontSize: 13, color: 'text.secondary' }}>
                Die Hälfte brauchte länger als {data.median_days === null ? '–' : `${formatNumber(data.median_days, Number.isInteger(data.median_days) ? 0 : 1)} Tage`} —
                nur Abonnenten, die sich je angemeldet haben
              </Box>
              <Box sx={{ height: 260, maxWidth: saeulenBreite(data.distribution.length) }}>
                <ResponsiveBar
                  data={data.distribution.map((d) => ({ ...d, anteil: d.share ?? 0 })) as unknown as BarDatum[]}
                  keys={['anteil']}
                  indexBy="label"
                  margin={{ top: 20, right: 8, bottom: schmal ? 76 : 56, left: 48 }}
                  padding={0.45}
                  borderRadius={4}
                  colors={[viz.series1]}
                  theme={viz.nivo}
                  animate={false}
                  enableGridX={false}
                  enableGridY
                  gridYValues={4}
                  enableLabel={false}
                  axisLeft={{ tickSize: 0, tickPadding: 6, tickValues: 4, format: achse('%') }}
                  axisBottom={{ tickSize: 0, tickPadding: 6, tickRotation: schmal ? -60 : -35 }}
                  tooltip={({ data: d }) => (
                    <Box>
                      <Box sx={{ fontWeight: 600, mb: 0.5 }}>{String(d.label)}</Box>
                      <TipRow color={viz.series1} label="der Anmelder" value={formatPct(Number(d.anteil))} />
                      <TipRow label="Personen" value={int(Number(d.n))} />
                    </Box>
                  )}
                  role="img"
                  ariaLabel="Verteilung der Tage bis zur ersten Webinar-Anmeldung"
                />
              </Box>
            </Box>
          </Box>
          <Hinweis>
            Kleine Zahl unter jeder Säule: {TERMS.webinareImFenster.label} (Termine zwischen Monatsanfang und 30 Tage nach Monatsende). Über die abgeschlossenen Monate haben sich {formatPct(data.summary.rate_window_mature)} der neuen Leads
            binnen {data.days_window} Tagen angemeldet ({int(data.summary.mature_within_window)} von{' '}
            {int(data.summary.mature_cohort)}). Gezählt werden nur Live-Anmeldungen (rechtzeitig oder Späteinlass). Weitere{' '}
            {int(data.summary.mature_registered_before)} dieser Leads waren schon vor ihrer Finanztrends-Anmeldung zu einem Webinar
            angemeldet und zählen nicht als „binnen 30 Tagen“.
            {data.summary.after_start_window > 0 &&
              // Audit 30.10.2026 B6a: das echte Kohortenfenster (erster voller Monat bis Datenstand), nicht „12 Monate“.
              ` ${int(data.summary.after_start_window)} Leads ${data.months.length > 0 ? `${formatMonat(data.months[0].month)}–${formatMonat(data.months[data.months.length - 1].month)}` : 'des Zeitraums'} haben sich in ihren 30 Tagen nur nach Sendestart angemeldet (meist für die Aufzeichnung) — sie zählen nicht als Anmeldung.`}
            {data.summary.within_window_doi > 0 &&
              ` Über den Webinar-Hinweis auf der Bestätigungsseite (seit 12.10.2026) ${data.summary.within_window_doi === 1 ? 'kam bisher 1 Anmeldung' : `kamen bisher ${int(data.summary.within_window_doi)} Anmeldungen`} binnen 30 Tagen.`}{' '}
            Die Verteilung
            der Tage zählt nur Eintrittsmonate, die mindestens 180 Tage zurückliegen (
            {data.distribution_months.map(formatMonat).join(', ')}) — jüngere könnten lange Abstände noch gar nicht
            haben. Nur Finanztrends: bei den Schwestermarken liegt der Eintritt meist nach den Anmeldungen.
          </Hinweis>
        </>
      )}
    </ChartCard>
  );
};

/**
 * Zahlenzeile über den Grafiken (P1-V3 § 2.7): Quote der abgeschlossenen Monate mit „x von y" und Zeitraum, der Median der
 * Tage (Bezug: alle, die sich je angemeldet haben) und die Anmeldungen über den Hinweis auf der Bestätigungsseite.
 */
const Zahlenzeile: FC<{ data: RegistrierungAntwort }> = ({ data }) => {
  const reif = data.months.filter((m) => m.is_mature);
  const s = data.summary;
  return (
    <Box sx={{ fontSize: 14, mb: 2, maxWidth: '80ch', lineHeight: 1.6 }}>
      <Box component="span" sx={{ fontWeight: 700 }}>
        {formatPct(s.rate_window_mature)}
      </Box>{' '}
      der neuen Newsletter-Abonnenten melden sich binnen {data.days_window} Tagen zu einem Webinar an ({int(s.mature_within_window)} von{' '}
      {int(s.mature_cohort)}
      {reif.length > 0 && `, Newsletter-Anmeldung ${formatMonat(reif[0].month)}–${formatMonat(reif[reif.length - 1].month)}`}).{' '}
      {data.median_days !== null && (
        <>
          Von allen, die sich bis heute überhaupt angemeldet haben, brauchte die Hälfte{' '}
          <Box component="span" sx={{ fontWeight: 700 }}>
            länger als {formatNumber(data.median_days, Number.isInteger(data.median_days) ? 0 : 1)} Tage
          </Box>
          <InfoTip text={`Bezug: alle, die sich irgendwann angemeldet haben (${int(data.distribution.reduce((a, d) => a + d.n, 0))} Personen aus älteren Monaten), nicht nur die binnen ${data.days_window} Tagen.`} />.{' '}
        </>
      )}
      Über den Webinar-Hinweis auf der Bestätigungsseite (seit 12.10.): {int(s.within_window_doi)} {s.within_window_doi === 1 ? 'Anmeldung' : 'Anmeldungen'}.
    </Box>
  );
};

export default RegistrierungKarte;
